import type { RealtimeToken } from './realtimeVoice';

const MAX_PREFACE_ITEMS = 20;
const MAX_PREFACE_CHARS = 4000;

export interface ArcturianPrefaceContent {
  type: 'input_text' | 'output_text';
  text: string;
}

export interface ArcturianPrefaceItem {
  type: 'message';
  role: 'user' | 'assistant';
  content: [ArcturianPrefaceContent];
}

export interface PreparedArcturianPreface {
  items: ArcturianPrefaceItem[];
  /** Temporary Phase-1 boundary. Phase 2 removes this adapter with the field. */
  throughRevision: number | null;
}

interface DataChannelWriter {
  send(data: string): void;
}

function parsePrefaceItem(value: unknown): ArcturianPrefaceItem {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('invalid_arcturian_preface_item');
  }
  const item = value as Record<string, unknown>;
  const role = item.role;
  if (item.type !== 'message' || (role !== 'user' && role !== 'assistant')) {
    throw new Error('invalid_arcturian_preface_item');
  }
  if (!Array.isArray(item.content) || item.content.length !== 1) {
    throw new Error('invalid_arcturian_preface_item');
  }
  const rawContent = item.content[0];
  if (!rawContent || typeof rawContent !== 'object' || Array.isArray(rawContent)) {
    throw new Error('invalid_arcturian_preface_item');
  }
  const content = rawContent as Record<string, unknown>;
  const expectedType = role === 'user' ? 'input_text' : 'output_text';
  if (content.type !== expectedType || typeof content.text !== 'string' || !content.text.trim()) {
    throw new Error('invalid_arcturian_preface_item');
  }
  return {
    type: 'message',
    role,
    content: [{ type: expectedType, text: content.text }],
  };
}

/**
 * Validate AiApi's temporary pre-Phase-2 replay contract at one removable
 * boundary. The server decides the history content; the browser only validates
 * and forwards the resulting OpenAI conversation items.
 */
export function prepareArcturianPreface(
  token: Pick<RealtimeToken, 'preface' | 'prefaced_through_revision'>,
  required: boolean,
): PreparedArcturianPreface {
  const hasItems = Object.prototype.hasOwnProperty.call(token, 'preface');
  const hasBoundary = Object.prototype.hasOwnProperty.call(token, 'prefaced_through_revision');
  if (!hasItems && !hasBoundary && !required) return { items: [], throughRevision: null };
  if (!hasItems || !hasBoundary || !Array.isArray(token.preface)) {
    throw new Error('invalid_arcturian_preface_contract');
  }
  if (token.preface.length > MAX_PREFACE_ITEMS) {
    throw new Error('invalid_arcturian_preface_contract');
  }
  const items = token.preface.map(parsePrefaceItem);
  const chars = items.reduce((sum, item) => sum + item.content[0].text.length, 0);
  if (chars > MAX_PREFACE_CHARS) throw new Error('invalid_arcturian_preface_contract');

  const boundary = token.prefaced_through_revision;
  const validBoundary = boundary === null
    || (Number.isInteger(boundary) && Number(boundary) >= 1);
  if (!validBoundary
      || (items.length === 0 && boundary !== null)
      || (items.length > 0 && boundary === null)) {
    throw new Error('invalid_arcturian_preface_contract');
  }
  return { items, throughRevision: boundary as number | null };
}

/** Exactly-once injector. It is armed before the first send, so a partial
 * DataChannel failure is terminal instead of being retried as duplicate history. */
export class ArcturianPrefaceInjector {
  private injected = false;

  constructor(private readonly preface: PreparedArcturianPreface) {}

  inject(writer: DataChannelWriter): boolean {
    if (this.injected) return false;
    this.injected = true;
    for (const item of this.preface.items) {
      writer.send(JSON.stringify({ type: 'conversation.item.create', item }));
    }
    return true;
  }
}
